import type { Contract } from '@essent/address';
import {
  EnergyType,
  ProductDuration,
  type TariffPriceGroup,
  type Tariffs,
  type TotalConsumptionPrice,
  type Costs,
} from '@essent/extension-offer';
import type {
  ConsumptionPrice,
  OfferOverview,
  PriceGroup,
} from '@innogy/become-a-customer/shared/interfaces';
import { format, startOfToday, startOfYesterday } from 'date-fns';
import { ContractType } from '@essent/common';

import type { CurrentContract } from './current-contract.reducer';

export const mapTariffsToContract = (
  tariffs: Tariffs,
  contracts: Contract[]
): CurrentContract => {
  const hasDynamicContract = contracts.some((contract) =>
    contract.productId?.endsWith('P')
  );
  const contractDuration = contracts[0].durationInYears as ProductDuration;

  let durationTitle = 'Vast';
  let contractType = ContractType.FIXED;
  if (hasDynamicContract) {
    durationTitle = 'Dynamisch';
    contractType = ContractType.DYNAMIC;
  } else if (contractDuration === ProductDuration.FLEX) {
    durationTitle = 'Variabel';
    contractType = ContractType.VARIABLE;
  }

  const productsTitle = contracts
    .map((contract) => contract.commodityProductDescription)
    .join(' & ');

  const commodities = Object.values(EnergyType).filter((energyType) =>
    tariffs.priceCostsPerConnection.some(
      (connection) => connection.energyType === energyType
    )
  );

  return {
    isCurrentContract: true,
    endDate: contracts[0].endDate,
    renewableDate: contracts[0].renewableStartDate,

    // Content
    durationTitle,
    productTitle: productsTitle,
    isDynamicProduct: hasDynamicContract,
    dynamicPricingTitle: hasDynamicContract ? durationTitle : '',
    productSubtitle: '',
    commodities,

    // Pricing
    beforeDiscountExpectedMonthlyAmount:
      tariffs.totalAmounts.totalMonthlyAmount,
    expectedMonthlyAmount: tariffs.totalAmounts.totalMonthlyAmount,
    beforeDiscountExpectedYearlyAmount: tariffs.totalAmounts.totalYearlyAmount,
    expectedYearlyAmount: tariffs.totalAmounts.totalYearlyAmount,
    // NOTE: the actual BBA is not linked to current tariffs or contract, but on the billing account
    budgetBillAmount: tariffs.totalAmounts.totalMonthlyAmount,
    offerOverviews: tariffs.priceCostsPerConnection.map(
      mapPriceCostsToOfferOverview
    ),

    // Defaults for all current contracts
    vatIncluded: true,
    offerId: '',
    campaignId: '',
    isHighlighted: false,
    usps: [],
    disclaimerContent: '',
    isCustomer: true,
    duration: contractDuration,
    contractType,
  };
};

function mapPriceCostsToOfferOverview(priceCosts: Costs): OfferOverview {
  const consumptionPrices =
    priceCosts.pricePeriods[0].totalConsumptionPrice?.map(
      mapPeriodPriceToConsumptionPrice
    );
  const priceGroups = priceCosts.pricePeriods[0].priceGroups.map(
    mapTariffPriceGroupToOfferPriceGroup
  );
  return {
    energyType: priceCosts.energyType as EnergyType,
    financialIncentives: [],
    consumptionPrices,
    priceGroups,
    totalPrice: {
      expectedYearlyAmount: priceGroups.reduce(
        (sum, group) => sum + group.totalPrice.expectedYearlyAmount,
        0
      ),
      expectedMonthlyAmount: priceGroups.reduce(
        (sum, group) => sum + group.totalPrice.expectedMonthlyAmount,
        0
      ),
    },
  };
}

function mapPeriodPriceToConsumptionPrice(
  price: TotalConsumptionPrice
): ConsumptionPrice {
  return {
    amount: price.amount,
    description: price.description,
    fromDate: format(startOfToday(), 'dd-MM'),
    toDate: format(startOfYesterday(), 'dd-MM'),
  };
}

function mapTariffPriceGroupToOfferPriceGroup(
  tariffPriceGroup: TariffPriceGroup
): PriceGroup {
  return {
    id: '',
    description: tariffPriceGroup.description,
    prices: tariffPriceGroup.prices.map((priceGroupPrice) => ({
      id: getPriceIdFromDescription(priceGroupPrice.description),
      description: priceGroupPrice.description,
      unitPrice: priceGroupPrice.unitPrice ?? 0,
      unit: priceGroupPrice.unitOfMeasurement ?? '',
      expectedPeriodAmount: priceGroupPrice.yearlyAmount ?? 0,
      fromDate: format(startOfToday(), 'dd-MM'),
      toDate: format(startOfYesterday(), 'dd-MM'),
    })),
    totalPrice: {
      expectedYearlyAmount: tariffPriceGroup.prices.reduce(
        (sum, price) => sum + (price.yearlyAmount ?? 0),
        0
      ),
      expectedMonthlyAmount: tariffPriceGroup.prices.reduce(
        (sum, price) => sum + (price.monthlyAmount ?? 0),
        0
      ),
    },
  };
}

function getPriceIdFromDescription(description: string): string {
  let id = '';

  if (/vaste leveringskosten/i.exec(description)) {
    id = '5';
  } else if (/vermindering energiebelasting/i.exec(description)) {
    id = '7';
  } else if (/korting Thuisvoordeel/i.exec(description)) {
    id = '8';
  } else if (/terugleverkosten/i.exec(description)) {
    id = '12';
  } else if (/variabele leveringskosten/i.exec(description)) {
    if (/daltarief/i.exec(description)) {
      id = '2';
    } else {
      id = '1';
    }
  } else if (/energiebelasting/i.exec(description)) {
    const zone = /zone (\d)/i.exec(description);
    if (zone) {
      id = zone[1];
    }
  }
  return id;
}
